import { api } from "../api";

export interface PatronType {
  id: string;
  tenantId: string;
  name: string;
  description?: string | null;
  permissions: string[];
  borrowLimit: number;
  renewalLimit: number;
  borrowingPeriodDays: number;
  fineRatePerDay?: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePatronTypeDto {
  name: string;
  description?: string;
  permissions: string[];
  borrowLimit: number;
  renewalLimit: number;
  borrowingPeriodDays: number;
  fineRatePerDay?: number;
  isActive?: boolean;
}

export type UpdatePatronTypeDto = Partial<CreatePatronTypeDto>;

export const patronTypesApi = {
  /**
   * Get all patron types for the current tenant (authenticated)
   */
  getAll: async (includeInactive = false): Promise<PatronType[]> => {
    const response = await api.get("/patron-types", {
      params: { includeInactive },
    });
    return response.data;
  },

  /**
   * Get active patron types for registration (public, domain-scoped)
   */
  getPublic: async (tenantSlug?: string): Promise<PatronType[]> => {
    const params: Record<string, string> = {
      domain: window.location.hostname,
    };
    // In development, only pass tenantSlug if explicitly provided
    // Backend will auto-fallback to first tenant if not provided
    if (tenantSlug && import.meta.env.DEV) {
      params.tenantSlug = tenantSlug;
    }
    const response = await api.get("/patron-types/public", { params });
    return response.data;
  },

  /**
   * Get a single patron type by ID
   */
  getById: async (id: string): Promise<PatronType> => {
    const response = await api.get(`/patron-types/${id}`);
    return response.data;
  },

  /**
   * Create a new patron type
   */
  create: async (data: CreatePatronTypeDto): Promise<PatronType> => {
    const response = await api.post("/patron-types", data);
    return response.data;
  },

  /**
   * Update an existing patron type
   */
  update: async (id: string, data: UpdatePatronTypeDto): Promise<PatronType> => {
    const response = await api.put(`/patron-types/${id}`, data);
    return response.data;
  },

  /**
   * Delete a patron type
   * @param id - Patron type ID
   * @param hardDelete - If true, permanently delete. If false or undefined, soft delete (set inactive).
   *                     When deleting from inactive view, use hardDelete=true to permanently remove.
   */
  delete: async (id: string, hardDelete?: boolean): Promise<void> => {
    const params = hardDelete ? { hardDelete: "true" } : {};
    await api.delete(`/patron-types/${id}`, { params });
  },
};
